import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGuest } from '../context/GuestContext';
import { useNavigate, useLocation } from 'react-router-dom';
import RegisterForm from '../components/RegisterForm';
import './LoginPage.css';

const API_URL = 'https://fifty-two-in-the-lou-production.up.railway.app/api';

const ForgotForm = ({ onBack, theme }) => {
  const [mode, setMode] = useState('password');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const endpoint = mode === 'password' ? 'forgot-password' : 'forgot-username';
      await fetch(`${API_URL}/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setStep(mode === 'password' ? 'code' : 'usernameSent');
    } catch (err) {
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code, newPassword })
      });
      if (res.ok) {
        setStep('done');
      } else {
        const msg = await res.text();
        setError(msg || 'Invalid or expired code.');
      }
    } catch (err) {
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    marginBottom: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '0.95rem',
    boxSizing: 'border-box'
  };

  return (
    <div className="login-container" style={{ backgroundColor: theme.headerBackground }}>
      <div className="login-box" style={{ backgroundColor: theme.cardBackground }}>
        <img
          src="/logos/3.4_LightAccent_Yellow_blue52_blackWEEKS.png"
          alt="52 in the Lou"
          style={{ height: '60px', objectFit: 'contain', marginBottom: '8px', display: 'block', margin: '0 auto 8px auto' }}
        />
        <p className="login-subtitle">Account Recovery</p>
        {error && <div className="error-message">{error}</div>}

        {step === 'email' && (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button onClick={() => { setMode('password'); setError(''); }} style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                backgroundColor: mode === 'password' ? theme.buttonPrimary : theme.border,
                color: mode === 'password' ? theme.buttonText : theme.text,
                fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem'
              }}>Forgot Password</button>
              <button onClick={() => { setMode('username'); setError(''); }} style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                backgroundColor: mode === 'username' ? theme.buttonPrimary : theme.border,
                color: mode === 'username' ? theme.buttonText : theme.text,
                fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem'
              }}>Forgot Username</button>
            </div>
            <p style={{ color: theme.subText, fontSize: '0.85rem', marginBottom: '16px' }}>
              {mode === 'password'
                ? "Enter your email and we'll send a 6-digit reset code."
                : "Enter your email and we'll send your username."}
            </p>
            <form onSubmit={handleSendEmail}>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                required
              />
              <button type="submit" className="login-button"
                style={{ backgroundColor: theme.buttonPrimary, color: theme.buttonText }}
                disabled={loading}>
                {loading ? 'Sending...' : 'Send Recovery Email'}
              </button>
            </form>
          </>
        )}

        {step === 'code' && (
          <>
            <p style={{ color: theme.text, fontSize: '0.9rem', marginBottom: '16px', textAlign: 'center' }}>
              📧 Check your inbox at <strong>{email}</strong> for a 6-digit code.
            </p>
            <form onSubmit={handleResetPassword}>
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={inputStyle}
                maxLength={6}
                required
              />
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
                required
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                required
              />
              <button type="submit" className="login-button"
                style={{ backgroundColor: theme.buttonPrimary, color: theme.buttonText }}
                disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
            <p style={{ color: theme.subText, fontSize: '0.8rem', textAlign: 'center', marginTop: '8px' }}>
              Didn't get the email?{' '}
              <span onClick={() => setStep('email')} style={{ color: theme.buttonPrimary, cursor: 'pointer' }}>
                Try again
              </span>
            </p>
          </>
        )}

        {step === 'usernameSent' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: '2rem', marginBottom: '12px' }}>📧</p>
            <p style={{ color: theme.text, fontWeight: '700', marginBottom: '8px' }}>Email Sent!</p>
            <p style={{ color: theme.subText, fontSize: '0.85rem' }}>
              Check your inbox at <strong>{email}</strong> — your username has been sent.
            </p>
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: '2rem', marginBottom: '12px' }}>✅</p>
            <p style={{ color: theme.text, fontWeight: '700', marginBottom: '8px' }}>Password Reset!</p>
            <p style={{ color: theme.subText, fontSize: '0.85rem' }}>
              Your password has been updated. You can now log in.
            </p>
          </div>
        )}

        <p style={{ color: theme.subText, marginTop: '16px', textAlign: 'center', fontSize: '0.85rem' }}>
          <span onClick={onBack} style={{ color: theme.buttonPrimary, cursor: 'pointer' }}>
            ← Back to Login
          </span>
        </p>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showRegister, setShowRegister] = useState(location.state?.showRegister === true);
  const [showForgot, setShowForgot] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { theme } = useTheme();
  const { enterGuestMode } = useGuest();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(username, password);
      if (rememberMe) localStorage.setItem('rememberedUser', username);
    } catch (err) {
      setError('Invalid username or password');
    }
  };
 
  const handleGuestMode = () => {
    enterGuestMode();
    navigate('/');
  };

  if (showRegister) return <RegisterForm onBack={() => setShowRegister(false)} />;
  if (showForgot) return <ForgotForm onBack={() => setShowForgot(false)} theme={theme} />;

  return (
    <div className="login-container" style={{ backgroundColor: theme.headerBackground }}>
      <div className="login-box" style={{ backgroundColor: theme.cardBackground }}>
        <img
          src="/logos/52-logo-horiz.png"
          alt="52 in the Lou"
          style={{ height: '60px', objectFit: 'contain', marginBottom: '8px', display: 'block', margin: '0 auto 8px auto' }}
        />
        <p className="login-subtitle">St. Louis Bucket List</p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleLogin}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="login-input"
            style={{ backgroundColor: theme.background, color: theme.text, borderColor: theme.border }}
            required
          />
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            style={{ backgroundColor: theme.background, color: theme.text, borderColor: theme.border }}
            required
          />
          <div className="remember-me" style={{ color: theme.text }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="rememberMe">Remember me</label>
          </div>
          <button type="submit" className="login-button"
            style={{ backgroundColor: theme.buttonPrimary, color: theme.buttonText }}>
            Login
          </button>
        </form>

        <button
          onClick={handleGuestMode}
          style={{
            marginTop: '10px',
            background: '#EEC170',
            border: 'none',
            color: '#000000',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Guest Mode
        </button>

        <p onClick={() => setShowForgot(true)} style={{
          color: theme.subText, fontSize: '0.85rem',
          textAlign: 'center', cursor: 'pointer', marginTop: '12px'
        }}>
          Forgot username or password?
        </p>
        <p className="register-link" style={{ color: theme.subText }}>
          Don't have an account?{' '}
          <span onClick={() => setShowRegister(true)} style={{ color: theme.buttonPrimary }}>
            Create one
          </span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;